import { CheckIcon, SearchIcon } from '@primer/octicons-react';
import React, { useRef, useState } from 'react';
import { graphql } from 'react-relay';

import { DeliveryOrderXlsxImportDialog_deliveryOrderXlsxImportMutation } from '../../../relay/__generated__/DeliveryOrderXlsxImportDialog_deliveryOrderXlsxImportMutation.graphql';
import Button from '../../core/Button';
import Dialog, { DialogProps } from '../../core/Dialog';
import FormLayout from '../../core/FormLayout';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import Text from '../../core/Text';
import UploadField from '../../core/UploadField';
import View from '../../core/View';
import DeliveryOrderXlsxResultView from '../DeliveryOrderXlsxResultView';

type ImportResult = DeliveryOrderXlsxImportDialog_deliveryOrderXlsxImportMutation['response']['deliveryOrderXlsxImport'];

type Props = {} & DialogProps &
  Pick<
    MutationFormikProps<DeliveryOrderXlsxImportDialog_deliveryOrderXlsxImportMutation>,
    'config' | 'onSubmit' | 'connections'
  >;

const XLSX_ACCEPT = 'application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const DeliveryOrderXlsxImportDialog = ({ config, onSubmit, connections, ...props }: Props) => {
  const commitRef = useRef(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const errorCount = result?.issues.filter(({ kind }) => kind === 'error').length ?? 0;
  const canCommit = !!result && errorCount === 0 && result.validCount > 0;

  return (
    <Dialog {...props}>
      <Dialog.Header>
        <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>운송장 일괄 업데이트</Text>
        <View>
          <Text sx={{ fontSize: 1, fontWeight: 'normal', color: 'fg.subtle' }}>
            택배사에서 받은 운송장 xlsx 파일을 올리면 기존 배송 건에 송장을 달고 배송 완료로 바꿔요
          </Text>
        </View>
      </Dialog.Header>
      <MutationFormik<DeliveryOrderXlsxImportDialog_deliveryOrderXlsxImportMutation>
        mutation={graphql`
          mutation DeliveryOrderXlsxImportDialog_deliveryOrderXlsxImportMutation(
            $input: DeliveryOrderXlsxImportInput!
          ) {
            deliveryOrderXlsxImport(input: $input) {
              committed
              readByHeader
              totalCount
              validCount
              updatedCount
              issues {
                row
                kind
                field
                message
              }
            }
          }
        `}
        initialValues={{ file: { key: '', size: 0 } }}
        valuesTransformer={(values) => ({ ...values, commit: commitRef.current })}
        config={{
          ...config,
          onCompleted: (response, errors, formikHelpers) => {
            if (response.deliveryOrderXlsxImport.committed) {
              config?.onCompleted?.(response, errors, formikHelpers);
              return;
            }
            setResult(response.deliveryOrderXlsxImport);
          },
        }}
        onSubmit={onSubmit}
        connections={connections}
      >
        {({ dirty, handleSubmit }, { scrollContainerRef, nonFieldErrorRef }) => (
          <>
            <Dialog.Body ref={scrollContainerRef}>
              <NonFieldError ref={nonFieldErrorRef} sx={{ marginBottom: 3 }} />
              <FormLayout>
                <UploadField
                  label={'파일'}
                  name={'file'}
                  required
                  accept={XLSX_ACCEPT}
                  onCompletedUpload={() => setResult(null)}
                />
              </FormLayout>
              {result ? (
                <DeliveryOrderXlsxResultView
                  totalCount={result.totalCount}
                  issues={result.issues}
                  notice={
                    result.readByHeader
                      ? '첫 줄의 열 제목을 보고 읽었어요.'
                      : '첫 줄에 아는 열 제목이 없어 순서대로 읽었어요. 열 순서가 양식과 같은지 확인해 주세요.'
                  }
                />
              ) : null}
            </Dialog.Body>
            <Dialog.Footer>
              {canCommit ? (
                <Button
                  variant={'primary'}
                  leadingIcon={CheckIcon}
                  onClick={() => {
                    commitRef.current = true;
                    handleSubmit();
                  }}
                >
                  {`${result?.validCount}건 반영하기`}
                </Button>
              ) : (
                <Button
                  disabled={!dirty}
                  variant={'primary'}
                  leadingIcon={SearchIcon}
                  onClick={() => {
                    commitRef.current = false;
                    handleSubmit();
                  }}
                >
                  검사하기
                </Button>
              )}
            </Dialog.Footer>
          </>
        )}
      </MutationFormik>
    </Dialog>
  );
};

export default DeliveryOrderXlsxImportDialog;
